/**
 * MJCF/XML parser facade for MuJoCo robot files.
 * Domain-specific parsing lives in src/core/mjcf-parser modules.
 */
import type {
  Vector4,
  Site,
  SiteUUID,
  MeshUUID,
  Joint,
  JointUUID,
  Vector2,
  Vector3,
  Vector5,
  Equality,
  EqualityUUID,
  ContactExclude,
  ContactPair,
  BodyUUID,
  WorldBody,
  SceneCamera,
  CameraUUID,
  SceneLight,
  LightUUID,
  TendonSpatialRef,
  TendonUUID,
  Sensor,
  SensorUUID,
} from "../types/scene";
import { generateUuid } from "../utils/id";
import { parseMjcfOption } from "../utils/mjcfOptions";
import { sanitizeMjcfName } from "../utils/mjcfNames";
import { logger } from "../utils/logger";
import { parseMjcfCompilerSettings, parseMjcfStatisticSettings } from "../stores/mjcfSettingsStore";
import { parsePassthroughAttributes as parsePassthroughAttributesFromElement } from "./mjcf-parser/attributeUtils";
import {
  parseHFields as parseHFieldsFromDocument,
  parseMeshDefinitions,
  parseTextures as parseTexturesFromDocument,
} from "./mjcf-parser/assets";
import {
  convertSpecularShininessToMetallicRoughness as convertSpecularShininessToMetallicRoughnessValue,
  parseColorVector as parseColorVectorValue,
  parseMaterials as parseMaterialsFromDocument,
  resolveMaterialProp as resolveMaterialPropFromState,
} from "./mjcf-parser/materials";
import {
  parseKeyframeDetails as parseKeyframeDetailsFromDocument,
  parseKeyframes as parseKeyframesFromDocument,
  parsePreservedMjcfSections as parsePreservedMjcfSectionsFromDocument,
  parseReferencedFiles as parseReferencedFilesFromDocument,
  detectUnsupportedPluginSurfaceWarnings,
} from "./mjcf-parser/documentSections";
import {
  parseContactExcludes as parseContactExcludesFromDocument,
  parseContactPairs as parseContactPairsFromDocument,
  parseSensors as parseSensorsFromDocument,
} from "./mjcf-parser/contactsSensors";
import { parseActuators as parseActuatorsFromDocument } from "./mjcf-parser/actuators";
import {
  getInheritedCollisionType as getInheritedCollisionTypeFromState,
  getInheritedGeomProp as getInheritedGeomPropFromState,
  isGeomVisualOnly as isGeomVisualOnlyFromState,
  parseBodyGeoms as parseBodyGeomsFromElement,
  resolveChildClass,
} from "./mjcf-parser/bodyGeoms";
import { parseCameraNode as parseCameraNodeFromElement } from "./mjcf-parser/cameras";
import { parseLightNode as parseLightNodeFromElement } from "./mjcf-parser/lights";
import {
  flattenBodies as flattenBodyTree,
  getBodies as getBodiesFromDocument,
  parseBodyCameras as parseBodyCamerasFromElement,
  parseBodies as parseBodiesFromElement,
  parseBodyInertia as parseBodyInertiaFromElement,
  parseBodyJointUuids as parseBodyJointUuidsFromElement,
  parseBodyPlugins as parseBodyPluginsFromElement,
  parseBodySiteUuids as parseBodySiteUuidsFromElement,
  parseBodyTransform as parseBodyTransformFromElement,
  parseWorldbodySites as parseWorldbodySitesFromDocument,
  resolveBodyUuidAndName as resolveBodyUuidAndNameFromElement,
} from "./mjcf-parser/bodyHierarchy";
import { parseEqualities as parseEqualitiesFromDocument } from "./mjcf-parser/equalities";
import {
  createJointFromParsedData as createJointFromParsedDataWithState,
  getResolvedJointElementName as getResolvedJointElementNameFromState,
  parseJoints as parseJointsFromDocument,
  resolveJointProp as resolveJointPropFromState,
  resolveJointScalarProp as resolveJointScalarPropFromState,
  resolveJointType as resolveJointTypeFromState,
} from "./mjcf-parser/joints";
import {
  parseSpatialTendonRoute as parseSpatialTendonRouteFromDocument,
  parseTendons as parseTendonsFromDocument,
  resolveTendonAttributes as resolveTendonAttributesFromDocument,
} from "./mjcf-parser/tendons";
import {
  extractActuatorAttrs as extractActuatorAttrsFromElement,
  type ActuatorDefaultsState,
  parseActuatorDefaults as parseActuatorDefaultsForState,
  parseCollisionDefaults as parseCollisionDefaultsForState,
  parseDefaults as parseDefaultsForState,
  parseEqualityDefaults as parseEqualityDefaultsForState,
  parseJointDefaults as parseJointDefaultsForState,
  type JointDefaultsState,
  type MaterialDefaultsState,
  parseMaterialDefaults as parseMaterialDefaultsForState,
  parseTendonDefaults as parseTendonDefaultsForState,
} from "./mjcf-parser/defaults";
import type { ParsedJointData } from "./mjcf-parser/joints";
import type {
  AmbiguousGeomRoleCandidate,
  MJCFParseResult,
  ParsedActuatorsMap,
  ParsedBodiesMap,
  ParsedBodyNode,
  ParsedGeom,
  ParsedHFieldsMap,
  ParsedInertia,
  ParsedKeyframesMap,
  ParsedMaterialsMap,
  ParsedMeshesMap,
  ParsedTendonsMap,
  ParsedTexturesMap,
} from "./mjcf-parser/types";

type ParsedMeshDefinitionWithUuid = ParsedMeshesMap[string] & { uuid?: unknown };

export {
  AMBIGUOUS_GEOM_ROLE_WARNING_PREFIX,
  shouldEmitSyntheticVisualForMeshCollisionAttrs,
} from "./mjcf-parser/geomRole";
export { UNSUPPORTED_PLUGIN_SURFACE_WARNING_PREFIX } from "./mjcf-parser/documentSections";
export type {
  AmbiguousGeomRoleCandidate,
  MJCFParseResult,
  ParsedActuator,
  ParsedActuatorsMap,
  ParsedBodiesMap,
  ParsedBody,
  ParsedBodyNode,
  ParsedGeom,
  ParsedHFieldsMap,
  ParsedInertia,
  ParsedKeyframe,
  ParsedKeyframesMap,
  ParsedMaterialsMap,
  ParsedMeshesMap,
  ParsedTexture,
  ParsedTexturesMap,
  ParsedTendon,
  ParsedTendonJointRef,
  ParsedTendonsMap,
} from "./mjcf-parser/types";

export class MJCFParser {
  xmlDoc!: Document;
  classDefaults: Record<string, unknown>;
  classScaleMap: Record<string, number[]>;
  collisionTypeMap: Record<string, string>;
  classParentMap: Record<string, string>;
  mainDefaultScale: number[];
  jointClassDefaults: JointDefaultsState["jointClassDefaults"];
  jointDefaultScope: JointDefaultsState["jointDefaultScope"];
  collisionClassDefaults: Record<string, unknown>;
  materialClassDefaults: MaterialDefaultsState["materialClassDefaults"];
  materialDefaultScope: MaterialDefaultsState["materialDefaultScope"];
  geomClassDefaults: Record<
    string,
    {
      material?: string;
      pos?: number[];
      quat?: number[];
      size?: number[];
      fromto?: number[];
      type?: string;
      mesh?: string;
      hfield?: string;
      mass?: number;
      density?: number;
      friction?: number[];
      solref?: number[];
      solimp?: Vector5;
      contype?: number;
      conaffinity?: number;
      condim?: number;
      group?: number;
    }
  >;
  equalityClassDefaults: Record<string, { solref?: Vector2; solimp?: Vector5 }>;
  equalityDefaultScope: { solref?: Vector2; solimp?: Vector5 };
  tendonClassDefaults: Record<string, Record<string, string>>;
  tendonDefaultScope: Record<string, string>;
  cameraClassDefaults: Record<
    string,
    {
      pos?: number[];
      quat?: number[];
      euler?: number[];
      axisangle?: number[];
      xyaxes?: number[];
      zaxis?: number[];
    }
  >;
  lightClassDefaults: Record<string, Record<string, string>>;
  actuatorClassDefaults: ActuatorDefaultsState["actuatorClassDefaults"];
  actuatorClassParentMap: Record<string, string>;
  actuatorTypeDefaults: ActuatorDefaultsState["actuatorTypeDefaults"];
  meshDefinitions: ParsedMeshesMap;
  hfieldDefinitions: ParsedHFieldsMap;
  // Global counter to assign stable, unique names to unnamed geoms across the entire parse
  globalGeomCounter: number;
  // Global counter for visual-only unnamed/default-named geoms
  globalVisualCounter: number;
  // Global counter for inertials
  globalInertialCounter: number;
  // Map joint names to their UUIDs for lookup during body parsing
  jointNameToUuid: Record<string, JointUUID>;
  // Map joint names to their ranges for inheritrange actuator resolution
  jointNameToRange: Record<string, [number, number]>;
  // Resolved names for joint elements whose MJCF syntax omits one, notably unnamed freejoints.
  resolvedJointElementNames: WeakMap<Element, string>;
  // Sites map to collect all sites during parsing
  sitesMap: Record<string, Site>;
  camerasMap: Record<string, SceneCamera>;
  lightsMap: Record<string, SceneLight>;
  // Compiler settings
  eulerseq: string;
  angleUnit: "radian" | "degree";
  ambiguousGeomRoleCandidates: AmbiguousGeomRoleCandidate[];
  parseWarnings: string[];

  constructor() {
    this.classDefaults = {};
    this.meshDefinitions = {};
    this.hfieldDefinitions = {};
    this.classScaleMap = {};
    this.collisionTypeMap = {};
    this.classParentMap = {};
    this.mainDefaultScale = [1, 1, 1];
    this.jointClassDefaults = {};
    this.jointDefaultScope = {};
    this.collisionClassDefaults = {};
    this.materialClassDefaults = {};
    this.materialDefaultScope = {};
    this.geomClassDefaults = {}; // Store geom defaults like material from default classes
    this.equalityClassDefaults = {};
    this.equalityDefaultScope = {};
    this.tendonClassDefaults = {};
    this.tendonDefaultScope = {};
    this.cameraClassDefaults = {};
    this.lightClassDefaults = {};
    this.actuatorClassDefaults = {};
    this.actuatorClassParentMap = {};
    this.actuatorTypeDefaults = {}; // e.g., classless <general> defaults at some scope
    this.globalGeomCounter = 1;
    this.globalVisualCounter = 1;
    this.globalInertialCounter = 1;
    this.jointNameToUuid = {};
    this.jointNameToRange = {};
    this.resolvedJointElementNames = new WeakMap();
    this.sitesMap = {};
    this.camerasMap = {};
    this.lightsMap = {};
    this.eulerseq = "xyz";
    this.angleUnit = "radian";
    this.ambiguousGeomRoleCandidates = [];
    this.parseWarnings = [];
  }

  warn(...args: unknown[]): void {
    const message = args
      .map((arg) => {
        if (typeof arg === "string") return arg;
        try {
          return JSON.stringify(arg);
        } catch {
          return String(arg);
        }
      })
      .join(" ");
    if (message.trim()) this.parseWarnings.push(message);
    logger.warn(...args);
  }

  // Walk ancestor body elements to find the nearest childclass attribute.
  _resolveChildClass(bodyElem: Element): string | null {
    return resolveChildClass(bodyElem);
  }

  coerceUuidFromMjcfName(value: string | null): string | null {
    if (!value) return null;
    const s = String(value).trim();
    if (!s) return null;
    // Accept either a raw UUID, or our sanitized form (e.g. "b_<uuid>").
    const maybe = s.startsWith("b_") ? s.slice(2) : s;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(maybe)
      ? maybe
      : null;
  }

  /**
   * Parse a complete MJCF file and return all parsed data
   * @param {File} xmlFile - The XML file to parse
   * @returns {Promise<Object>} Parsed data object
   */
  async parseFile(xmlFile: File): Promise<MJCFParseResult> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        this.parseWarnings = [];
        try {
          this.meshDefinitions = {};
          // Reset global geom counter at the start of each parse
          this.globalGeomCounter = 1;
          this.globalVisualCounter = 1;
          this.globalInertialCounter = 1;
          this.sitesMap = {};
          this.camerasMap = {};
          this.lightsMap = {};
          this.cameraClassDefaults = {};
          this.lightClassDefaults = {};
          this.ambiguousGeomRoleCandidates = [];
          if (typeof reader.result !== "string") {
            reject(new Error("Failed to read XML file as text"));
            return;
          }
          const parser = new DOMParser();
          this.xmlDoc = parser.parseFromString(reader.result, "application/xml");

          // Check for parsing errors
          const parseError = this.xmlDoc.querySelector("parsererror");
          if (parseError) {
            logger.error("[MJCFParser] XML parsing error:", parseError.textContent);
            reject(new Error("XML parsing failed"));
            return;
          }

          // Read compiler settings first; eulerseq and angle unit affect all subsequent parsing.
          const compilerElem = this.xmlDoc.querySelector("compiler");
          this.eulerseq = compilerElem?.getAttribute("eulerseq") || "xyz";
          this.angleUnit = (compilerElem?.getAttribute("angle") as "radian" | "degree") || "radian";
          detectUnsupportedPluginSurfaceWarnings(this.xmlDoc).forEach((warning) =>
            this.warn(warning)
          );

          // Parse all components
          this.parseDefaults();
          this.parseMeshScales();
          this.parseHFields();
          const materials = this.parseMaterials();
          const joints = this.parseJoints();
          // Build name-to-UUID mapping for joints
          this.jointNameToUuid = {};
          this.jointNameToRange = {};
          joints.forEach((joint) => {
            if (joint.name) {
              this.jointNameToUuid[joint.name] = joint.uuid;
              if ("range" in joint && Array.isArray(joint.range)) {
                this.jointNameToRange[joint.name] = joint.range as [number, number];
              }
            }
          });
          const { bodiesMap, worldBodies } = this.getBodies();
          const textures = this.parseTextures();
          const keyframeDetails = this.parseKeyframeDetails();
          const keyframes: Record<string, number[]> = {};
          Object.entries(keyframeDetails).forEach(([name, detail]) => {
            keyframes[name] = detail.qpos;
          });
          const preservedMjcfSections = this.parsePreservedMjcfSections();
          const tendons = this.parseTendons();
          const tendonNameToUuid: Record<string, TendonUUID> = {};
          Object.values(tendons).forEach((tendon) => {
            if (!tendon?.name) return;
            tendonNameToUuid[tendon.name] = tendon.uuid as TendonUUID;
          });
          const { actuators } = this.parseActuators(tendonNameToUuid);
          // Convert joints array to map for UUID lookup
          const jointsMap: Record<string, Joint> = {};
          joints.forEach((joint) => {
            jointsMap[joint.uuid] = joint;
          });
          const equalities = this.parseEqualities(bodiesMap, jointsMap, this.sitesMap, tendons);
          const contactPairs = this.parseContactPairs(bodiesMap);
          const contactExcludes = this.parseContactExcludes(bodiesMap);
          const sensors = this.parseSensors();
          const option = parseMjcfOption(this.xmlDoc);
          const mjcfCompiler = parseMjcfCompilerSettings(this.xmlDoc);
          const mjcfStatistic = parseMjcfStatisticSettings(this.xmlDoc);
          const remapMeshesToUuid = (
            bodies: ParsedBodiesMap,
            meshes: ParsedMeshesMap
          ): ParsedMeshesMap => {
            if (!meshes || Object.keys(meshes).length === 0) return meshes;
            const nameToUuid = new Map<string, string>();
            const meshesByUuid: ParsedMeshesMap = {};
            Object.entries(meshes).forEach(([name, meshDef]) => {
              if (!meshDef) return;
              const meshWithUuid = meshDef as ParsedMeshDefinitionWithUuid;
              const preferred = meshWithUuid.uuid ? String(meshWithUuid.uuid) : "";
              const uuid = (preferred || generateUuid()) as MeshUUID;
              nameToUuid.set(name, uuid);
              meshesByUuid[uuid] = {
                ...meshDef,
                uuid,
                name: meshDef.name || name,
              };
            });
            Object.values(bodies).forEach((body) => {
              if (!body || !Array.isArray(body.geoms)) return;
              body.geoms.forEach((geom) => {
                if (geom && typeof geom.mesh === "string") {
                  const mapped = nameToUuid.get(geom.mesh);
                  if (mapped) geom.mesh = mapped;
                }
              });
            });
            return meshesByUuid;
          };
          this.meshDefinitions = remapMeshesToUuid(bodiesMap, this.meshDefinitions);
          Object.values(this.camerasMap).forEach((camera) => {
            const targetName =
              typeof camera?.targetBodyName === "string" ? camera.targetBodyName : "";
            if (!targetName) return;
            const match = Object.entries(bodiesMap).find(
              ([, body]) =>
                body &&
                (String(body.name || "").trim() === targetName ||
                  sanitizeMjcfName(String(body.name || "")) === sanitizeMjcfName(targetName))
            );
            if (match) {
              camera.targetBodyUuid = match[0] as BodyUUID;
            }
          });
          Object.values(this.lightsMap).forEach((light) => {
            const targetName = typeof light?.targetBodyName === "string" ? light.targetBodyName : "";
            if (!targetName) return;
            const match = Object.entries(bodiesMap).find(
              ([, body]) =>
                body &&
                (String(body.name || "").trim() === targetName ||
                  sanitizeMjcfName(String(body.name || "")) === sanitizeMjcfName(targetName))
            );
            if (match) {
              light.targetBodyUuid = match[0] as BodyUUID;
            }
          });

          resolve({
            xmlDoc: this.xmlDoc,
            parseWarnings: this.parseWarnings,
            ambiguousGeomRoleCandidates: this.ambiguousGeomRoleCandidates,
            joints,
            bodies: bodiesMap,
            worldBodies,
            sites: this.sitesMap,
            cameras: this.camerasMap as Record<CameraUUID, SceneCamera>,
            lights: this.lightsMap as Record<LightUUID, SceneLight>,
            materials,
            textures,
            actuators,
            tendons,
            meshes: this.meshDefinitions,
            hfields: this.hfieldDefinitions,
            collisionClassDefaults: this.collisionClassDefaults,
            collisionTypeMap: this.collisionTypeMap,
            referencedFiles: this.parseReferencedFiles(),
            keyframes,
            keyframeDetails,
            preservedMjcfSections,
            equalities,
            contactPairs,
            contactExcludes,
            sensors,
            option,
            mjcfCompiler,
            mjcfStatistic,
          });
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsText(xmlFile);
    });
  }

  /**
   * Parse <default> sections for class inheritance and defaults
   */
  parseDefaults(): void {
    parseDefaultsForState(this, this.xmlDoc);
  }

  /**
   * Parse collision-related defaults (mesh scales, collision types, class inheritance)
   */
  parseCollisionDefaults(
    defaultElem: Element,
    parentScale: number[] | null = null,
    classMap: Record<string, number[]> = {},
    typeMap: Record<string, string> = {},
    parentClass: string | null = null,
    parentMap: Record<string, string> = {}
  ): void {
    parseCollisionDefaultsForState(
      this,
      defaultElem,
      parentScale,
      classMap,
      typeMap,
      parentClass,
      parentMap
    );
  }

  /**
   * Parse actuator defaults (like <general> within <default>) with class inheritance
   */
  parseActuatorDefaults(
    defaultElem: Element,
    parentClass: string | null = null,
    inherited: Record<string, unknown> = {}
  ): void {
    parseActuatorDefaultsForState(this, defaultElem, parentClass, inherited);
  }

  extractActuatorAttrs(node: Element): Record<string, unknown> {
    return extractActuatorAttrsFromElement(node);
  }

  /**
   * Parse joint-related defaults
   */
  parseJointDefaults(defaultElem: Element, parentClass: string | null = null): void {
    parseJointDefaultsForState(this, defaultElem, parentClass);
  }

  /**
   * Parse material-related defaults from <default> sections
   */
  parseMaterialDefaults(defaultElem: Element, parentClass: string | null = null): void {
    parseMaterialDefaultsForState(this, defaultElem, parentClass);
  }

  /**
   * Parse equality defaults from <default> sections.
   */
  parseEqualityDefaults(defaultElem: Element, parentClass: string | null = null): void {
    parseEqualityDefaultsForState(this, defaultElem, parentClass);
  }

  /**
   * Parse mesh scale information from <asset> section
   */
  parseMeshScales() {
    this.meshDefinitions = parseMeshDefinitions({
      xmlDoc: this.xmlDoc,
      classScaleMap: this.classScaleMap,
      classParentMap: this.classParentMap,
      existingMeshes: this.meshDefinitions,
    });
  }

  parseHFields(): ParsedHFieldsMap {
    this.hfieldDefinitions = parseHFieldsFromDocument(this.xmlDoc);
    return this.hfieldDefinitions;
  }
  /**
   * Convert legacy specular/shininess to modern metallic/roughness
   * This is a common conversion used in modern PBR workflows
   * @param {number} specular - Specular value (0-1)
   * @param {number} shininess - Shininess value (0-1)
   * @returns {Object} Object with metallic and roughness values
   */
  convertSpecularShininessToMetallicRoughness(
    specular: number,
    shininess: number
  ): { metallic: number; roughness: number } {
    return convertSpecularShininessToMetallicRoughnessValue(specular, shininess);
  }

  parseColorVector(value: string | null): Vector4 | undefined {
    return parseColorVectorValue(value);
  }

  /**
   * Resolve material property from class inheritance chain
   */
  resolveMaterialProp(materialElem: Element, prop: string): number | undefined {
    return resolveMaterialPropFromState(this, materialElem, prop);
  }

  /**
   * Parse materials from <asset> section
   */
  parseMaterials(): ParsedMaterialsMap {
    return parseMaterialsFromDocument(this);
  }

  /**
   * Parse textures from <asset> section
   */
  parseTextures(): ParsedTexturesMap {
    return parseTexturesFromDocument(this.xmlDoc);
  }

  parseKeyframeDetails(): ParsedKeyframesMap {
    return parseKeyframeDetailsFromDocument(this.xmlDoc);
  }

  /**
   * Parse keyframes from <keyframe> section
   */
  parseKeyframes(): Record<string, number[]> {
    return parseKeyframesFromDocument(this.xmlDoc);
  }

  parsePreservedMjcfSections(): { custom?: string; extension?: string; lights?: string[] } {
    return parsePreservedMjcfSectionsFromDocument(this.xmlDoc);
  }

  /**
   * Parse referenced files (includes, meshes, textures, and hfields)
   */
  parseReferencedFiles(): string[] {
    return parseReferencedFilesFromDocument(this.xmlDoc);
  }

  /**
   * Resolve joint property from class inheritance chain
   */
  resolveJointProp(jointElem: Element, prop: string): number[] | undefined {
    return resolveJointPropFromState(this, jointElem, prop);
  }

  /**
   * Resolve scalar joint property from class inheritance chain (for properties like stiffness, springref)
   */
  resolveJointScalarProp(jointElem: Element, prop: string): number | undefined {
    return resolveJointScalarPropFromState(this, jointElem, prop);
  }

  /**
   * Resolve joint type from class inheritance chain
   */
  resolveJointType(jointElem: Element): "hinge" | "slide" | "ball" | "free" {
    return resolveJointTypeFromState(this, jointElem);
  }

  /**
   * Create a complete Joint object from parsed data with all required properties
   */
  createJointFromParsedData(
    name: string,
    kind: "free" | "ball" | "hinge" | "slide",
    parsedData: ParsedJointData,
    uuidOverride?: JointUUID | null
  ): Joint {
    return createJointFromParsedDataWithState(this, name, kind, parsedData, uuidOverride);
  }

  parseTendonDefaults(defaultElem: Element, parentClass: string | null = null): void {
    parseTendonDefaultsForState(this, defaultElem, parentClass);
  }

  getResolvedJointElementName(jointElem: Element, fallbackIndex = 0): string | null {
    return getResolvedJointElementNameFromState(this, jointElem, fallbackIndex);
  }

  /**
   * Parse all joints from the XML
   */
  parseJoints(): Joint[] {
    return parseJointsFromDocument(this);
  }

  /**
   * Parse <tendon> section and build tendon map
   */
  parsePassthroughAttributes(
    node: Element,
    skipNames: string[] = []
  ): Record<string, string> | undefined {
    return parsePassthroughAttributesFromElement(node, skipNames);
  }

  resolveTendonAttributes(node: Element): Record<string, string> | undefined {
    return resolveTendonAttributesFromDocument(this, node);
  }

  parseSpatialTendonRoute(node: Element, tendonName: string): TendonSpatialRef[] {
    return parseSpatialTendonRouteFromDocument(this, node, tendonName);
  }

  parseTendons(): ParsedTendonsMap {
    return parseTendonsFromDocument(this);
  }

  /**
   * Parse <actuator> section and build actuator map and joint->actuator mapping
   */
  parseActuators(tendonNameToUuid: Record<string, TendonUUID> = {}): {
    actuators: ParsedActuatorsMap;
  } {
    return parseActuatorsFromDocument(this, tendonNameToUuid);
  }

  parseCameraNode(
    cameraElem: Element,
    defaults: { kind: "fixed" | "mounted"; mountBodyUuid?: BodyUUID }
  ): SceneCamera | null {
    return parseCameraNodeFromElement(this, cameraElem, defaults);
  }

  parseLightNode(
    lightElem: Element,
    defaults: { kind: "fixed" | "mounted"; mountBodyUuid?: BodyUUID }
  ): SceneLight | null {
    return parseLightNodeFromElement(this, lightElem, defaults);
  }

  resolveBodyUuidAndName(bodyElem: Element): {
    name: string | null;
    bodyUuid: BodyUUID;
    parsedBodyName: string;
  } {
    return resolveBodyUuidAndNameFromElement(this, bodyElem);
  }

  isGeomVisualOnly(geomElem: Element, className: string | null): boolean {
    return isGeomVisualOnlyFromState(this, geomElem, className);
  }

  parseBodyGeoms(bodyElem: Element, name: string | null): ParsedGeom[] {
    return parseBodyGeomsFromElement(this, bodyElem, name);
  }

  parseBodyJointUuids(bodyElem: Element, name: string | null): JointUUID[] {
    return parseBodyJointUuidsFromElement(this, bodyElem, name);
  }

  parseBodySiteUuids(bodyElem: Element, name: string | null): SiteUUID[] {
    return parseBodySiteUuidsFromElement(this, bodyElem, name);
  }

  parseBodyPlugins(bodyElem: Element): string[] | undefined {
    return parseBodyPluginsFromElement(bodyElem);
  }

  parseWorldbodySites(): void {
    parseWorldbodySitesFromDocument(this);
  }

  parseBodyCameras(bodyElem: Element, bodyUuid: BodyUUID): void {
    parseBodyCamerasFromElement(this, bodyElem, bodyUuid);
  }

  parseBodyTransform(bodyElem: Element): { position: Vector3; orientation: Vector4 } {
    return parseBodyTransformFromElement(this, bodyElem);
  }

  parseBodyInertia(bodyElem: Element): ParsedInertia | undefined {
    return parseBodyInertiaFromElement(this, bodyElem);
  }

  parseBodies(bodyElem: Element): ParsedBodyNode {
    return parseBodiesFromElement(this, bodyElem);
  }

  /**
   * Get inherited collision type from class hierarchy
   */
  getInheritedCollisionType(className: string): string | null {
    return getInheritedCollisionTypeFromState(this, className);
  }

  /**
   * Get inherited geom property from class hierarchy
   * Walks up the class inheritance chain to find the first defined value
   */
  getInheritedGeomProp(
    className: string | null,
    prop:
      | "pos"
      | "quat"
      | "size"
      | "fromto"
      | "type"
      | "mesh"
      | "hfield"
      | "material"
      | "mass"
      | "density"
      | "friction"
      | "solref"
      | "solimp"
      | "contype"
      | "conaffinity"
      | "condim"
      | "group"
  ): unknown {
    return getInheritedGeomPropFromState(this, className, prop);
  }

  /**
   * Flatten body tree into a Record and collect world-level body UUIDs
   * Converts children from Body[] to BodyUUID[]
   */
  flattenBodies(bodies: ParsedBodyNode[]): {
    bodiesMap: ParsedBodiesMap;
    worldBodies: WorldBody[];
  } {
    return flattenBodyTree(bodies);
  }

  /**
   * Get the complete body tree starting from worldbody
   */
  getBodies(): { bodiesMap: ParsedBodiesMap; worldBodies: WorldBody[] } {
    return getBodiesFromDocument(this);
  }

  /**
   * Parse equality constraints from <equality> section
   * @param bodiesMap Map of bodies by UUID (for name->UUID lookup)
   * @param jointsMap Map of joints by UUID (for name->UUID lookup)
   * @param sitesMap Map of sites by UUID (for name->UUID lookup)
   */
  parseEqualities(
    bodiesMap: ParsedBodiesMap,
    jointsMap: Record<string, Joint>,
    sitesMap: Record<string, Site>,
    tendonsMap: ParsedTendonsMap
  ): Record<EqualityUUID, Equality> {
    return parseEqualitiesFromDocument(this, bodiesMap, jointsMap, sitesMap, tendonsMap);
  }

  /**
   * Parse contact exclusion pairs from <contact><exclude> elements.
   * Maps body names to UUIDs using the parsed bodies map.
   */
  parseContactExcludes(bodiesMap: ParsedBodiesMap): ContactExclude[] {
    return parseContactExcludesFromDocument(this.xmlDoc, bodiesMap, (...args) =>
      this.warn(...args)
    );
  }

  /**
   * Parse explicit contact pair overrides from <contact><pair> elements.
   * Maps MuJoCo geom names back to geom UUIDs so pair settings survive import/export.
   */
  parseContactPairs(bodiesMap: ParsedBodiesMap): ContactPair[] {
    return parseContactPairsFromDocument(this.xmlDoc, bodiesMap, (...args) => this.warn(...args));
  }

  parseSensors(): Record<SensorUUID, Sensor> {
    return parseSensorsFromDocument(this.xmlDoc);
  }
}
